import { useId, useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectTerminalHeight,
  selectTerminalOpen,
  terminalClosed,
  terminalOpened,
  terminalResized,
  terminalToggled,
  TERMINAL_MIN_HEIGHT,
} from '../features/ui/uiSlice';

export interface TerminalTab {
  readonly id: string;
  readonly label: string;
  /** A marker next to the label, e.g. for a failed build or output still arriving. */
  readonly badge?: 'error' | 'busy';
  readonly content: ReactNode;
}

export interface TerminalDrawerProps {
  readonly tabs: readonly TerminalTab[];
  readonly activeTab: string;
  readonly onSelect: (id: string) => void;
}

/** Height change per arrow key press when resizing with the keyboard. */
const RESIZE_STEP = 32;
/** The terminal never takes more than this share of the window. */
const MAX_SHARE = 0.8;

const BADGE_TEXT = { error: 'failed', busy: 'in progress' } as const;

/**
 * Collapsible, resizable panel at the bottom of the workspace with terminal-like tabs. The tab bar
 * is always visible; choosing a tab opens the panel. Tabs follow the ARIA tabs pattern (one Tab
 * stop, arrows/Home/End move between them). Escape inside the panel collapses it and returns focus
 * to the active tab.
 */
export function TerminalDrawer({ tabs, activeTab, onSelect }: TerminalDrawerProps) {
  const open = useAppSelector(selectTerminalOpen);
  const height = useAppSelector(selectTerminalHeight);
  const dispatch = useAppDispatch();
  const section = useRef<HTMLElement>(null);
  const tabButtons = useRef(new Map<string, HTMLButtonElement>());
  const id = useId();
  const active = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];
  const maxHeight = Math.max(TERMINAL_MIN_HEIGHT, Math.round(globalThis.innerHeight * MAX_SHARE));
  // the stored height may predate a smaller window: never cover more than the share allowed
  const shownHeight = Math.min(height, maxHeight);
  const resize = (next: number) => dispatch(terminalResized(Math.min(next, maxHeight)));
  const focusTab = (tabId: string | undefined) => {
    if (tabId !== undefined) {
      tabButtons.current.get(tabId)?.focus();
    }
  };
  const collapse = () => {
    dispatch(terminalClosed());
    focusTab(active?.id); // the panel (and whatever had focus in it) goes away
  };
  const choose = (tabId: string) => {
    onSelect(tabId);
    dispatch(terminalOpened());
  };
  const onTabKey = (event: KeyboardEvent, index: number) => {
    const last = tabs.length - 1;
    const target = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last }[
      event.key
    ];
    const tab = target === undefined ? undefined : tabs[target];
    if (tab !== undefined) {
      event.preventDefault();
      choose(tab.id);
      focusTab(tab.id);
    }
  };

  const onSeparatorKey = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      resize(height + (event.key === 'ArrowUp' ? RESIZE_STEP : -RESIZE_STEP));
    }
  };
  const onSeparatorDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onSeparatorMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.buttons === 1 && section.current !== null) {
      resize(section.current.getBoundingClientRect().bottom - event.clientY);
    }
  };

  return (
    <section ref={section} className="terminal" data-open={open} aria-label="Terminal" style={open ? { height: shownHeight } : undefined}>
      {open && (
        <div
          className="terminal-resize"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize terminal"
          aria-valuemin={TERMINAL_MIN_HEIGHT}
          aria-valuemax={maxHeight}
          aria-valuenow={shownHeight}
          tabIndex={0}
          onKeyDown={onSeparatorKey}
          onPointerDown={onSeparatorDown}
          onPointerMove={onSeparatorMove}
        />
      )}
      <div className="terminal-bar">
        <div role="tablist" aria-label="Terminal tabs" className="terminal-tabs">
          {tabs.map((tab, index) => {
            const selected = tab.id === active?.id;
            return (
              <button
                key={tab.id}
                ref={(element) => {
                  if (element === null) {
                    tabButtons.current.delete(tab.id);
                  } else {
                    tabButtons.current.set(tab.id, element);
                  }
                }}
                id={`${id}-tab-${tab.id}`}
                type="button"
                role="tab"
                className="terminal-tab"
                aria-selected={selected}
                aria-controls={open && selected ? `${id}-panel` : undefined}
                tabIndex={selected ? 0 : -1}
                onClick={() => choose(tab.id)}
                onKeyDown={(event) => onTabKey(event, index)}
              >
                {tab.label}
                {tab.badge !== undefined && (
                  <span className="terminal-badge">
                    <span aria-hidden="true">{tab.badge === 'error' ? '✗' : '…'}</span>
                    <span className="visually-hidden"> ({BADGE_TEXT[tab.badge]})</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="terminal-toggle"
          aria-expanded={open}
          aria-controls={open ? `${id}-panel` : undefined}
          aria-label={open ? 'Collapse terminal' : 'Expand terminal'}
          onClick={() => (open ? collapse() : dispatch(terminalToggled()))}
        >
          {open ? '▾' : '▴'}
        </button>
      </div>
      {open && active !== undefined && (
        <div
          id={`${id}-panel`}
          role="tabpanel"
          aria-labelledby={`${id}-tab-${active.id}`}
          className="terminal-panel"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation(); // collapse the terminal, not the side drawer
              collapse();
            }
          }}
        >
          {active.content}
        </div>
      )}
    </section>
  );
}
